import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../utils/firebaseConfig";
import {
  FaCheck,
  FaTimes,
  FaUsers,
  FaEnvelope,
  FaTrash,
} from "react-icons/fa";

const STATUS_LABEL = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
};

const isDev = process.env.NODE_ENV === "development";

// HTML escape so any user-supplied text is safe to embed in email markup.
const esc = (s) =>
  String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );

const formatDate = (d) => {
  if (!d) return "";
  // Firestore Timestamp -> Date
  const date = typeof d.toDate === "function" ? d.toDate() : new Date(d);
  if (isNaN(date)) return "";
  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const renderCustomerEmail = ({ kind, reservation, adminNote }) => {
  const niceDate = esc(formatDate(reservation.Date));
  const greeting = `<p>Hi ${esc(reservation.Name)},</p>`;
  const guests = parseInt(reservation.Persons, 10) || 0;
  const guestsText = `${guests} guest${guests > 1 ? "s" : ""}`;
  const noteBlock = adminNote
    ? `<p><em>A note from us:</em><br/>${esc(adminNote).replace(
        /\n/g,
        "<br/>"
      )}</p>`
    : "";
  const signature = `<p>Warm regards,<br/>The Old House team</p>`;

  if (kind === "accepted") {
    return {
      subject: "Your reservation is confirmed",
      html: `${greeting}
        <p>Great news — your table for <strong>${guestsText}</strong> on
        <strong>${niceDate}</strong> is <strong>confirmed</strong>.</p>
        <p>We will hold your table for fifteen minutes past the
        reservation time. If anything changes on your end, just reply
        to this email.</p>
        ${noteBlock}
        <p>We look forward to seeing you!</p>
        ${signature}`,
    };
  }

  // rejected
  return {
    subject: "About your reservation request",
    html: `${greeting}
      <p>Thank you for your interest in <strong>The Old House</strong>.
      Unfortunately we are unable to confirm your request for
      <strong>${guestsText}</strong> on <strong>${niceDate}</strong>
      at this time.</p>
      ${noteBlock}
      <p>We hope to welcome you another time — please feel free to
      try a different date.</p>
      ${signature}`,
  };
};

export default function ManageReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [actionTarget, setActionTarget] = useState(null); // {reservation, kind}
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Real-time listener so admin sees new requests without refreshing.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "reservations"),
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const data = d.data();
          // Skip pure email payload docs (customer pending / accept / reject emails).
          if (data._emailOnly) return;
          // Skip any stray non-reservation docs.
          if (!data.Name) return;
          rows.push({ id: d.id, ...data });
        });
        // Newest pending first; accepted/rejected sorted by reservation date.
        rows.sort((a, b) => {
          const da =
            (a.Date_created && a.Date_created.toDate?.()) ||
            new Date(a.Date_created || 0);
          const db_ =
            (b.Date_created && b.Date_created.toDate?.()) ||
            new Date(b.Date_created || 0);
          return db_ - da;
        });
        setReservations(rows);
        setLoading(false);
      },
      (err) => {
        console.error("reservations listener error", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const grouped = useMemo(() => {
    const g = { pending: [], accepted: [], rejected: [] };
    for (const r of reservations) {
      const s = r.status || "pending";
      if (g[s]) g[s].push(r);
      else g.pending.push(r);
    }
    return g;
  }, [reservations]);

  const openAction = (reservation, kind) => {
    setActionTarget({ reservation, kind });
    setNoteText("");
  };

  const deleteReservation = async (reservation) => {
    const ok = window.confirm(
      "Delete this reservation for " +
        reservation.Name +
        "?\n\nThis cannot be undone."
    );
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "reservations", reservation.id));
      if (isDev) console.log("[admin] deleted reservation id=" + reservation.id);
    } catch (err) {
      console.error("[admin] delete failed:", err);
      alert("Could not delete. Please try again.");
    }
  };

  const closeAction = () => {
    if (submitting) return;
    setActionTarget(null);
    setNoteText("");
  };

  const confirmAction = async () => {
    if (!actionTarget) return;
    const { reservation, kind } = actionTarget;
    const newStatus = kind === "accept" ? "accepted" : "rejected";
    setSubmitting(true);
    try {
      if (isDev) console.log(
        `[admin] ${newStatus} reservation id=${reservation.id}` +
          (noteText.trim() ? " (with note)" : "")
      );

      // 1. Update the reservation status + admin note.
      await updateDoc(doc(db, "reservations", reservation.id), {
        status: newStatus,
        adminNote: noteText.trim(),
        decidedAt: serverTimestamp(),
      });
      if (isDev) console.log(`[admin] reservation ${reservation.id} status → ${newStatus}`);

      // 2. Send the customer email via an email-only doc in the same
      //    collection (the Firebase Trigger-Email extension picks this up).
      const { subject, html } = renderCustomerEmail({
        kind: newStatus,
        reservation,
        adminNote: noteText.trim(),
      });
      const emailRef = await addDoc(collection(db, "reservations"), {
        _emailOnly: true,
        to: [reservation.Email],
        message: { subject, html },
        relatedReservation: reservation.id,
        Date_created: new Date(),
      });
      if (isDev) console.log(
        `[admin] customer ${newStatus}-email doc written: id=${emailRef.id}`
      );

      closeAction();
    } catch (err) {
      console.error("[admin] Failed to update reservation:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: "pending", label: "Pending", count: grouped.pending.length },
    { key: "accepted", label: "Accepted", count: grouped.accepted.length },
    { key: "rejected", label: "Rejected", count: grouped.rejected.length },
  ];

  const rows = grouped[activeTab] || [];

  return (
    <div className="manage_reservations">
      <div className="reservations_tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            className={`reservations_tab${
              activeTab === t.key ? " is-active" : ""
            }`}
            onClick={() => setActiveTab(t.key)}
          >
            <span>{t.label}</span>
            <span className="reservations_tab__count">{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="reservations_empty">Loading reservations…</p>
      ) : rows.length === 0 ? (
        <p className="reservations_empty">No {activeTab} reservations.</p>
      ) : (
        <ul className="reservations_list">
          {rows.map((r) => (
            <li key={r.id} className="reservation_row" data-status={r.status}>
              <div className="reservation_row__main">
                <div className="reservation_row__head">
                  <h4 className="reservation_row__name">{r.Name}</h4>
                  <span
                    className="reservation_row__status"
                    data-status={r.status || "pending"}
                  >
                    {STATUS_LABEL[r.status || "pending"]}
                  </span>
                </div>
                <p className="reservation_row__date">{formatDate(r.Date)}</p>
                <div className="reservation_row__meta">
                  <span>
                    <FaUsers /> {r.Persons}
                  </span>
                  <span>
                    <FaEnvelope />{" "}
                    <a href={`mailto:${r.Email}`}>{r.Email}</a>
                  </span>
                </div>
                {r.adminNote && (
                  <p className="reservation_row__note">
                    <em>Note:</em> {r.adminNote}
                  </p>
                )}
              </div>

              <div className="reservation_row__actions">
                {(!r.status || r.status === "pending") && (
                  <>
                    <button
                      type="button"
                      className="res_btn res_btn--accept"
                      onClick={() => openAction(r, "accept")}
                    >
                      <FaCheck /> Accept
                    </button>
                    <button
                      type="button"
                      className="res_btn res_btn--reject"
                      onClick={() => openAction(r, "reject")}
                    >
                      <FaTimes /> Reject
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="res_btn res_btn--delete"
                  onClick={() => deleteReservation(r)}
                  title="Delete reservation"
                  aria-label="Delete reservation"
                >
                  <FaTrash />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {actionTarget && (
        <div
          className="reservation_modal__overlay"
          onClick={closeAction}
          role="presentation"
        >
          <div
            className="reservation_modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              {actionTarget.kind === "accept"
                ? "Accept reservation"
                : "Reject reservation"}
            </h3>
            <p className="reservation_modal__subtitle">
              <strong>{actionTarget.reservation.Name}</strong> —{" "}
              {formatDate(actionTarget.reservation.Date)} ·{" "}
              {actionTarget.reservation.Persons} guests
            </p>
            <label className="reservation_modal__label">
              Optional note for the customer
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
                placeholder={
                  actionTarget.kind === "accept"
                    ? "e.g. We'll prepare the corner table for you."
                    : "e.g. We're fully booked that evening, but Sunday is available."
                }
              />
            </label>
            <div className="reservation_modal__actions">
              <button
                type="button"
                className="res_btn res_btn--ghost"
                onClick={closeAction}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`res_btn ${
                  actionTarget.kind === "accept"
                    ? "res_btn--accept"
                    : "res_btn--reject"
                }`}
                onClick={confirmAction}
                disabled={submitting}
              >
                {submitting
                  ? "Sending…"
                  : actionTarget.kind === "accept"
                  ? "Confirm & email customer"
                  : "Reject & email customer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
